import {
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";

type Props = {
  value: string;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  menuStyles?: React.CSSProperties;
  disabled?: boolean;
};

const PhoneField = ({
  value,
  onChange,
  fullWidth = true,
  error,
  helperText,
  menuStyles,
  disabled,
}: Props) => {
  // edit formatting/masking for finland to accept Moi's longer phone numbers
  const excludedCountries = ["fi"];

  // filter out finland from the defaultCountries array
  const filteredCountries = defaultCountries.filter(
    (country) => !excludedCountries.includes(country[1]),
  );

  // add masking for Finland country manually
  filteredCountries.push(["Finland", "fi", "358", ".. ... .. ..."]);

  const sanitizePhoneNumber = (phone: string, countryDialCode: string) => {
    if (phone === `+${countryDialCode}` || phone.trim() === "") {
      // prevent updating only the dial code
      onChange("");
    } else {
      onChange(phone);
    }
  };

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: "fi",
      value,
      forceDialCode: true,
      countries: filteredCountries,
      onChange: (data) => sanitizePhoneNumber(data.phone, country.dialCode),
    });

  return (
    <TextField
      disabled={disabled}
      value={inputValue}
      onChange={handlePhoneValueChange}
      type="tel"
      size="small"
      fullWidth={fullWidth}
      inputRef={inputRef}
      error={error}
      helperText={error ? helperText : ""}
      InputProps={{
        startAdornment: (
          <InputAdornment
            position="start"
            style={{ marginRight: "2px", marginLeft: "-8px" }}
          >
            <Select
              MenuProps={{
                style: {
                  ...menuStyles,
                  height: "350px",
                  left: "-34px",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              }}
              sx={{
                width: "60px",
                // Remove default outline (display only on focus)
                fieldset: {
                  display: "none",
                  top: "1px",
                  width: "60px",
                  height: "39px",
                },
                '&.Mui-focused:has(div[aria-expanded="false"])': {
                  fieldset: {
                    display: "block",
                  },
                },
                // Update default spacing
                ".MuiSelect-select": {
                  padding: "8px",
                  paddingRight: "24px !important",
                },
                svg: {
                  right: 0,
                },
              }}
              value={country.iso2}
              onChange={(e) => setCountry(e.target.value)}
              renderValue={(value) => (
                <FlagImage iso2={value} style={{ display: "flex" }} />
              )}
            >
              {/* DROPDOWN LIST */}
              {defaultCountries.map((c) => {
                const country = parseCountry(c);
                return (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    <FlagImage
                      iso2={country.iso2}
                      style={{ marginRight: "8px", maxWidth: "24px" }}
                    />
                    <Typography marginRight="8px">{country.name}</Typography>
                    <Typography color="gray">+{country.dialCode}</Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default PhoneField;
